import { X } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { Button } from '@/components/ui/button'

import { hasImgTags, parseAllImgContent } from '@/utils/emoji-parser'

import { MessageType } from '@/types/chat.type'

interface ReplyPreviewProps {
  replyingToMessage: {
    id: number
    content: string
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    type: string
    fileUrl?: string
    fileName?: string
  }
  onCancelReply: () => void
}

export const ReplyPreview: React.FC<ReplyPreviewProps> = ({ replyingToMessage, onCancelReply }) => {
  const truncateContent = (content: string, maxLength = 50) => {
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }

  const getFileIcon = (type: string) => {
    switch (type) {
      case MessageType.IMAGE:
        return '🖼️'
      case MessageType.FILE:
        return '📎'
      case MessageType.AUDIO:
        return '🎵'
      case MessageType.VIDEO:
        return '🎥'
      default:
        return '📄'
    }
  }

  // Render emoji content for text messages
  const renderEmojiContent = (content: string) => {
    if (!hasImgTags(content)) {
      return <span className="text-sm text-gray-700">{truncateContent(content, 50)}</span>
    }

    const segments = parseAllImgContent(content)
    const displaySegments = segments.slice(0, 4) // Show max 4 segments for reply preview
    const hasMore = segments.length > 4

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {displaySegments.map((segment, index) => {
          if (segment.type === 'text') {
            const text = segment.content.length > 25 ? segment.content.substring(0, 25) + '...' : segment.content
            return (
              <span key={index} className="text-sm text-gray-700">
                {text}
              </span>
            )
          } else if ((segment.type === 'custom-emoji' || segment.type === 'emoji') && segment.imageUrl) {
            return (
              <Image
                key={index}
                src={segment.imageUrl}
                alt={segment.alt || 'Image'}
                width={18}
                height={18}
                className="w-[18px] h-[18px] rounded object-cover"
              />
            )
          }
          return null
        })}
        {hasMore && <span className="text-sm text-gray-500">...</span>}
      </div>
    )
  }

  return (
    <div className="flex items-start gap-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 mb-1">
          <span className="text-xs font-medium text-blue-600">Replying to</span>
          <span className="text-xs text-gray-600">
            {replyingToMessage.sender.firstName} {replyingToMessage.sender.lastName}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {replyingToMessage.type === MessageType.IMAGE && replyingToMessage.fileUrl ? (
            <div className="flex items-center gap-2">
              <Image
                src={replyingToMessage.fileUrl}
                alt="Reply preview"
                width={20}
                height={20}
                className="w-5 h-5 rounded object-cover"
              />
              <span className="text-sm text-gray-600">Image</span>
            </div>
          ) : replyingToMessage.type !== MessageType.TEXT ? (
            <div className="flex items-center gap-1">
              <span className="text-sm">{getFileIcon(replyingToMessage.type)}</span>
              <span className="text-sm text-gray-600">{replyingToMessage.fileName || replyingToMessage.type}</span>
            </div>
          ) : (
            renderEmojiContent(replyingToMessage.content)
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onCancelReply}
        className="p-1 h-auto text-gray-500 hover:text-gray-700"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  )
}
